import { format, parseISO } from "date-fns";

class CompetitionService {
  constructor(apiBaseUrl) {
    this.apiBaseUrl = apiBaseUrl;
  }

  async fetchCompetitions() {
    try {
      console.log('Fetching competitions from:', `${this.apiBaseUrl}/competitions`);
      console.log('Using token:', localStorage.getItem("token") ? 'Token exists' : 'No token found');
      
      const response = await fetch(
        `${this.apiBaseUrl}/competitions`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      
      console.log('Response status:', response.status);
      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch competitions: ${response.status} ${response.statusText}`);
      }
      
      try {
        const data = JSON.parse(responseText);
        return this.enhanceCompetitionsWithParticipantDetails(data);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        throw new Error('Server returned invalid JSON response');
      }
    } catch (error) {
      console.error('Error in fetchCompetitions:', error);
      throw error;
    }
  }

  async enhanceCompetitionsWithParticipantDetails(competitions) {
    return await Promise.all(competitions.map(async (competition) => {
      if (!competition.participants || competition.participants.length === 0) {
        return competition;
      }

      const participantDetails = await Promise.all(
        competition.participants.map(async (participant) => {
          try {
            let detailsResponse;
            let votesResponse;

            // Fetch details based on participant type
            switch (participant.participantType) {
              case 'player':
                detailsResponse = await fetch(
                  `${this.apiBaseUrl}/players/${participant.participantId}`,
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                  }
                );
                break;
              case 'club':
                detailsResponse = await fetch(
                  `${this.apiBaseUrl}/clubs/${participant.participantId}`,
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                  }
                );
                break;
              case 'coach':
                detailsResponse = await fetch(
                  `${this.apiBaseUrl}/coaches/${participant.participantId}`,
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                  }
                );
                break;
              default:
                return participant;
            }

            // Fetch votes for all participant types
            votesResponse = await fetch(
              `${this.apiBaseUrl}/votes/participant/${participant.participantId}`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            );

            if (!detailsResponse.ok) {
              console.warn(`Failed to fetch details for ${participant.participantType} ${participant.participantId}`);
              return participant;
            }

            const detailsData = await detailsResponse.json();
            const votesData = votesResponse.ok ? await votesResponse.json() : { count: 0 };

            // Return enhanced participant data based on type
            switch (participant.participantType) {
              case 'player':
                return {
                  ...participant,
                  name: detailsData.name || `${detailsData.firstName} ${detailsData.lastName}`,
                  playerDetails: detailsData,
                  votes: votesData.count || 0
                };
              case 'club':
                return {
                  ...participant,
                  name: detailsData.name,
                  clubDetails: detailsData,
                  votes: votesData.count || 0
                };
              case 'coach':
                return {
                  ...participant,
                  name: detailsData.name || `${detailsData.firstName} ${detailsData.lastName}`,
                  coachDetails: detailsData,
                  votes: votesData.count || 0
                };
              default:
                return participant;
            }
          } catch (error) {
            console.warn(`Error fetching data for ${participant.participantType} ${participant.participantId}:`, error);
            return { ...participant, votes: 0 };
          }
        })
      );

      return {
        ...competition,
        participants: participantDetails
      };
    }));
  }

  async updateCompetition(competitionId, competitionData) {
    const response = await fetch(
      `${this.apiBaseUrl}/competitions/${competitionId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(competitionData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Update failed");
    }

    return response.json();
  }

  async deleteCompetition(competitionId) {
    const response = await fetch(`${this.apiBaseUrl}/competitions/${competitionId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Delete failed");
    }
  }

  async createCompetition(competitionData) {
    const response = await fetch(`${this.apiBaseUrl}/competitions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(competitionData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Creation failed");
    }

    return response.json();
  }

  async fetchCompetitionParticipants(competitionId, participantType) {
    const response = await fetch(
      `${this.apiBaseUrl}/competitions/${competitionId}/participants/${participantType}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch competition participants');
    }

    return response.json();
  }
}

export default CompetitionService; 